import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useHistoricalQuery } from './useHistoricalQuery.js';
import { ACTIVE_TILE_SOURCE } from '../../lib/tileSource.js';
import { tempColor } from '../../lib/tempColor.js';
import { RISK_BANDS, colorForRisk, riskLabel } from './historicalScale.js';
import { ModeToggle } from '../map/ControlPanel.js';
import type { MapMode } from '../map/types.js';

type PrefFeature = {
  type: 'Feature';
  geometry: { type: string; coordinates: unknown };
  properties: { code?: string; name?: string };
};
type PrefFC = { type: 'FeatureCollection'; features: PrefFeature[] };
type PointFC = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Point'; coordinates: [number, number] };
    properties: Record<string, string | number | null>;
  }>;
};

const POLY_SRC = 'hist-prefs';
const POLY_FILL = 'hist-prefs-fill';
const POLY_STROKE = 'hist-prefs-stroke';
const POLY_SEL = 'hist-prefs-selected';
const PT_SRC = 'hist-temp-pts';
const PT_CIRCLE = 'hist-temp-circle';
const PT_TEMP_LABEL = 'hist-temp-label';
const PT_DELTA_LABEL = 'hist-temp-delta';
const PREF_GEOJSON_URL = '/prefectures.geojson';
const EMPTY_PT_FC: PointFC = { type: 'FeatureCollection', features: [] };

// Week index (0-21) for today's JST month/day, using the same 5/1-start,
// 7-day-bin definition as the data. Out-of-season dates clamp to the ends.
export function currentHistoricalWeek(): number {
  const jst = new Date(Date.now() + 9 * 60 * 60 * 1000);
  const m = jst.getUTCMonth() + 1;
  const d = jst.getUTCDate();
  const start = Date.UTC(2025, 4, 1); // May 1 (year arbitrary; day diff only)
  const cur = Date.UTC(2025, m - 1, d);
  const delta = Math.floor((cur - start) / 86_400_000);
  if (delta < 0) return 0;
  return Math.min(Math.floor(delta / 7), 21);
}

// Latest season year — the sensible default when first opening past mode.
export const DEFAULT_HISTORICAL_YEAR = '2025';

export function HistoricalView({
  mode,
  onModeChange,
  year,
  weekIdx,
  onYearChange,
  onWeekChange,
}: {
  mode: MapMode;
  onModeChange: (m: MapMode) => void;
  // year / weekIdx are owned by MapPage so the selection survives switching
  // to WBGT / forecast and back (this component unmounts on mode switch).
  year: string;
  weekIdx: number;
  onYearChange: (y: string) => void;
  onWeekChange: (w: number) => void;
}) {
  const histQ = useHistoricalQuery();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);

  const [geo, setGeo] = useState<PrefFC | null>(null);
  const years = histQ.data?.years ?? [];
  const [selected, setSelected] = useState<{ code: string; name: string } | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  // Load GeoJSON once.
  useEffect(() => {
    let cancelled = false;
    fetch(PREF_GEOJSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error('geojson http ' + r.status);
        return r.json();
      })
      .then((j: PrefFC) => {
        if (!cancelled) setGeo(j);
      })
      .catch((err) => console.warn('prefectures.geojson failed', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // Observatory coordinates per prefecture (capital / main island). Used to
  // anchor the temp dot + popup on the populated mainland — for island-heavy
  // prefectures (Tokyo, Okinawa, Kagoshima) the polygon centroid falls
  // offshore among remote islands, which is misleading.
  const stationCoords = useMemo(() => {
    const m = new Map<string, [number, number]>();
    for (const p of histQ.data?.prefectures ?? []) {
      m.set(p.code, [p.lng, p.lat]);
    }
    return m;
  }, [histQ.data]);

  // Map init. Shares the saved view (center/zoom) with the forecast map so
  // switching modes keeps the user's position.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const saved = loadSavedView();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(),
      center: saved?.center ?? [137.5, 37.8],
      zoom: saved?.zoom ?? 4.3,
      attributionControl: false,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    let saveTimer: number | null = null;
    const persist = () => {
      if (saveTimer != null) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => {
        const c = map.getCenter();
        saveView({ center: [c.lng, c.lat], zoom: map.getZoom() });
      }, 400);
    };
    map.on('moveend', persist);
    map.on('zoomend', persist);
    mapRef.current = map;
    return () => {
      map.off('moveend', persist);
      map.off('zoomend', persist);
      if (saveTimer != null) window.clearTimeout(saveTimer);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Build risk-colored polygons.
  const polyFc = useMemo<PrefFC | null>(() => {
    if (!geo || !histQ.data) return geo;
    const wkBlock = histQ.data.data[year]?.[String(weekIdx)] ?? {};
    const feats = geo.features.map((f) => {
      const code = f.properties?.code;
      const cell = code ? wkBlock[code] : undefined;
      const risk = cell?.riskScore ?? null;
      return {
        ...f,
        properties: {
          ...f.properties,
          risk,
          color: colorForRisk(risk),
        },
      };
    });
    return { type: 'FeatureCollection', features: feats };
  }, [geo, histQ.data, year, weekIdx]);

  // Build temp dots (forecast-style) with week-over-week delta.
  const tempFc = useMemo<PointFC>(() => {
    if (!histQ.data || stationCoords.size === 0) return EMPTY_PT_FC;
    const curBlock = histQ.data.data[year]?.[String(weekIdx)] ?? {};
    const prevBlock = weekIdx > 0 ? histQ.data.data[year]?.[String(weekIdx - 1)] ?? {} : {};
    const features: PointFC['features'] = [];
    for (const [code, lnglat] of stationCoords.entries()) {
      const cell = curBlock[code];
      if (!cell || cell.maxTemp == null) continue;
      const t = cell.maxTemp;
      const prevT = prevBlock[code]?.maxTemp ?? null;
      let deltaLabel = '';
      let deltaColor = '#64748b';
      if (prevT != null) {
        const d = t - prevT;
        if (d > 0.05) {
          deltaLabel = `⬆${d.toFixed(1)}`;
          deltaColor = '#e60012';
        } else if (d < -0.05) {
          deltaLabel = `⬇${Math.abs(d).toFixed(1)}`;
          deltaColor = '#1d8eff';
        } else {
          deltaLabel = `±0.0`;
          deltaColor = '#64748b';
        }
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: lnglat },
        properties: {
          code,
          tmax: t,
          tempColor: tempColor(t),
          tempLabel: `${Math.round(t)}°`,
          deltaLabel,
          deltaColor,
        },
      });
    }
    return { type: 'FeatureCollection', features };
  }, [histQ.data, stationCoords, year, weekIdx]);

  // Single effect drives BOTH the risk polygons and the temp dots. Keeping
  // them in one effect (like the forecast MapView) means setData for both runs
  // under one isStyleLoaded() check — avoiding the two-effect race where one
  // source's setData/moveLayer flipped isStyleLoaded() false for the other.
  // One-time addSource/addLayer needs the style loaded (handled via 'load');
  // once the sources exist, setData runs every time without any guard.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !polyFc) return;

    const setupPoly = () => {
      map.addSource(POLY_SRC, {
        type: 'geojson',
        data: polyFc as unknown as GeoJSON.FeatureCollection,
      });
      map.addLayer({
        id: POLY_FILL,
        source: POLY_SRC,
        type: 'fill',
        paint: {
          'fill-color': ['get', 'color'] as unknown as maplibregl.ExpressionSpecification,
          'fill-opacity': 0.72,
        },
      });
      map.addLayer({
        id: POLY_STROKE,
        source: POLY_SRC,
        type: 'line',
        paint: { 'line-color': '#374151', 'line-width': 0.4 },
      });
      map.addLayer({
        id: POLY_SEL,
        source: POLY_SRC,
        type: 'line',
        filter: ['==', ['get', 'code'], ''] as unknown as maplibregl.FilterSpecification,
        paint: { 'line-color': '#0f172a', 'line-width': 2.4 },
      });
      map.on('click', POLY_FILL, (e) => {
        const f = e.features?.[0];
        if (!f) return;
        const p = f.properties as Record<string, unknown> | null;
        const code = (p?.code as string) ?? '';
        const name = (p?.name as string) ?? '';
        if (code) setSelected({ code, name });
      });
      // Tap outside any prefecture (sea / off-map) clears the selection, so the
      // popup dismisses — matching the WBGT / forecast tabs. Done with an
      // explicit hit-test instead of maplibre's closeOnClick to avoid an
      // event-ordering race with the prefecture click handler above.
      map.on('click', (e) => {
        const hit = map.queryRenderedFeatures(e.point, { layers: [POLY_FILL] });
        if (hit.length === 0) setSelected(null);
      });
      map.on('mouseenter', POLY_FILL, () => {
        map.getCanvas().style.cursor = 'pointer';
      });
      map.on('mouseleave', POLY_FILL, () => {
        map.getCanvas().style.cursor = '';
      });
    };

    const setupTemp = () => {
      map.addSource(PT_SRC, {
        type: 'geojson',
        data: tempFc as unknown as GeoJSON.FeatureCollection,
      });
      map.addLayer({
        id: PT_CIRCLE,
        source: PT_SRC,
        type: 'circle',
        paint: {
          'circle-color': ['get', 'tempColor'] as unknown as maplibregl.ExpressionSpecification,
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            4, 14,
            8, 18,
            12, 22,
          ],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
        },
      });
      map.addLayer({
        id: PT_TEMP_LABEL,
        source: PT_SRC,
        type: 'symbol',
        layout: {
          'text-field': ['get', 'tempLabel'] as unknown as maplibregl.ExpressionSpecification,
          'text-size': 15,
          'text-font': ['Noto Sans Regular'],
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: { 'text-color': '#ffffff' },
      });
      map.addLayer({
        id: PT_DELTA_LABEL,
        source: PT_SRC,
        type: 'symbol',
        layout: {
          'text-field': ['get', 'deltaLabel'] as unknown as maplibregl.ExpressionSpecification,
          'text-size': 13,
          'text-font': ['Noto Sans Regular'],
          'text-offset': [0, 1.35],
          'text-anchor': 'top',
          'text-allow-overlap': true,
          'text-ignore-placement': true,
        },
        paint: {
          'text-color': ['get', 'deltaColor'] as unknown as maplibregl.ExpressionSpecification,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.4,
        },
      });
    };

    const apply = () => {
      // One-time layer setup requires the style to be loaded.
      if (!map.getSource(POLY_SRC) || !map.getSource(PT_SRC)) {
        if (!map.isStyleLoaded()) {
          map.once('load', apply);
          return;
        }
        if (!map.getSource(POLY_SRC)) setupPoly();
        if (!map.getSource(PT_SRC)) setupTemp();
      }
      // Data refresh — safe to call on existing sources regardless of state.
      (map.getSource(POLY_SRC) as maplibregl.GeoJSONSource).setData(
        polyFc as unknown as GeoJSON.FeatureCollection,
      );
      (map.getSource(PT_SRC) as maplibregl.GeoJSONSource).setData(
        tempFc as unknown as GeoJSON.FeatureCollection,
      );
      // Keep temp layers above the risk fill.
      raiseTempLayers(map);
    };

    apply();
  }, [polyFc, tempFc]);

  // Highlight selected prefecture. setFilter is safe on an existing layer; the
  // getLayer guard covers the not-yet-set-up case.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer(POLY_SEL)) return;
    map.setFilter(POLY_SEL, [
      '==',
      ['get', 'code'],
      selected?.code ?? '',
    ] as unknown as maplibregl.FilterSpecification);
  }, [selected]);

  // Rank of selected prefecture by risk in current week.
  const rankInfo = useMemo(() => {
    if (!histQ.data || !selected) return null;
    const wkBlock = histQ.data.data[year]?.[String(weekIdx)] ?? {};
    const entries = Object.entries(wkBlock)
      .map(([code, cell]) => ({ code, risk: cell.riskScore ?? -1 }))
      .filter((x) => x.risk >= 0)
      .sort((a, b) => b.risk - a.risk);
    const idx = entries.findIndex((x) => x.code === selected.code);
    if (idx < 0) return null;
    return { rank: idx + 1, total: entries.length };
  }, [histQ.data, selected, year, weekIdx]);

  // Popup.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selected || !histQ.data) {
      popupRef.current?.remove();
      popupRef.current = null;
      return;
    }
    const cell = histQ.data.data[year]?.[String(weekIdx)]?.[selected.code];
    const prevCell =
      weekIdx > 0 ? histQ.data.data[year]?.[String(weekIdx - 1)]?.[selected.code] : undefined;
    const week = histQ.data.weeks[weekIdx];
    const center = stationCoords.get(selected.code);
    if (!center || !cell) return;
    const pop = histQ.data.populations[selected.code];
    const html = renderPopupHtml({
      code: selected.code,
      year,
      weekLabel: week?.label ?? '',
      prefName: selected.name || selected.code,
      maxTemp: cell.maxTemp,
      prevTemp: prevCell?.maxTemp ?? null,
      risk: cell.riskScore,
      transports: cell.transports,
      population: pop,
      rank: rankInfo?.rank ?? null,
      total: rankInfo?.total ?? null,
    });
    // Reuse the existing popup (update position + content) rather than
    // remove/recreate. Calling .remove() would fire 'close', which our
    // handler turns into setSelected(null) — that would wrongly dismiss the
    // popup on every slider/year change. With reuse, 'close' only fires on a
    // real user action (X button or closeOnClick tap outside).
    if (popupRef.current) {
      popupRef.current.setLngLat(center).setHTML(html);
    } else {
      const popup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: false,
        offset: 14,
        maxWidth: '280px',
      })
        .setLngLat(center)
        .setHTML(html)
        .addTo(map);
      popup.on('close', () => {
        popupRef.current = null;
        setSelected(null);
      });
      popupRef.current = popup;
    }
  }, [selected, histQ.data, year, weekIdx, stationCoords, rankInfo]);

  const week = histQ.data?.weeks[weekIdx];

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="absolute inset-0" />

      {/* Top-left: risk legend (WBGT-style) + year pulldown */}
      <RiskLegend year={year} years={years} onYearChange={onYearChange} />

      {/* Bottom control bar — matches the forecast ControlPanel chrome */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 pb-safe">
        <div className="pointer-events-auto mx-auto max-w-2xl bg-white/95 px-3 pt-2 pb-2 shadow-[0_-6px_24px_rgba(15,23,42,0.18)] backdrop-blur sm:m-3 sm:rounded-2xl">
          <ModeToggle mode={mode} onChange={onModeChange} />
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[10px] leading-tight text-slate-500">{year}年 過去実績</span>
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="ml-auto inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 active:bg-slate-200"
              aria-label="指標の説明・出典"
            >
              <span className="text-base font-semibold">ℹ</span>
            </button>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium text-slate-700">
              {week ? `${year}年 ${week.label}` : '—'}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">
              最高気温は県庁所在地等の観測値
            </span>
          </div>
          <div className="heat-slider relative mt-1.5">
            <input
              type="range"
              min={0}
              max={Math.max(0, (histQ.data?.weeks.length ?? 1) - 1)}
              value={weekIdx}
              onChange={(e) => onWeekChange(parseInt(e.target.value, 10))}
              className="heat-slider__input relative h-2 w-full cursor-pointer rounded-full"
              aria-label="週スライダー"
            />
          </div>
        </div>
      </div>

      {histQ.isLoading && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/40">
          <span className="rounded bg-white/95 px-3 py-1 text-xs text-slate-700 shadow">
            読み込み中…
          </span>
        </div>
      )}
      {histQ.isError && (
        <div className="absolute inset-x-3 top-24 z-20 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-800 shadow">
          過去データの取得に失敗しました。電波状況を確認してリロードしてください。
        </div>
      )}

      {infoOpen && <InfoModal data={histQ.data} onClose={() => setInfoOpen(false)} />}
    </div>
  );
}

function RiskLegend({
  year,
  years,
  onYearChange,
}: {
  year: string;
  years: number[];
  onYearChange: (y: string) => void;
}) {
  return (
    <div className="pointer-events-auto absolute left-3 top-3 z-10 rounded-md bg-white/90 px-3 py-2 text-xs shadow-md backdrop-blur">
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="font-semibold text-slate-700">年</span>
        <select
          value={year}
          onChange={(e) => onYearChange(e.target.value)}
          className="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs text-slate-800"
          aria-label="年を選択"
        >
          {years.map((y) => (
            <option key={y} value={String(y)}>
              {y}年
            </option>
          ))}
        </select>
      </div>
      <p className="font-semibold text-slate-700">熱中症搬送リスク度</p>
      <p className="mb-1 text-[9px] leading-tight text-slate-500">熱中症搬送・人口10万対・週</p>
      <ul className="space-y-0.5">
        {RISK_BANDS.map((b) => (
          <li key={b.label} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ backgroundColor: b.color }}
              aria-hidden
            />
            <span className="text-slate-700">{b.label}</span>
            <span className="ml-auto pl-2 text-slate-400">
              {b.min === -Infinity ? `〜${b.max}` : b.max === Infinity ? `${b.min}〜` : `${b.min}–${b.max}`}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-1.5 flex items-center gap-1.5 border-t border-slate-100 pt-1.5 text-[10px] text-slate-600">
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-[#e60012] text-[9px] font-bold text-white">
          °
        </span>
        <span>週の最高気温</span>
      </div>
    </div>
  );
}

function InfoModal({
  data,
  onClose,
}: {
  data: import('@heat-pwa/types').HistoricalData | undefined;
  onClose: () => void;
}) {
  const formula = data?.riskScoreFormula ?? '週内搬送人員 ÷ 県人口 × 100,000（人口10万対・週）';
  const popSrc = data?.populationSource;
  return (
    <div
      className="absolute inset-0 z-30 flex items-end justify-center bg-black/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[85%] w-full overflow-y-auto rounded-t-2xl bg-white p-5 shadow-xl sm:max-w-md sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">指標の見方・算出方法</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
            aria-label="閉じる"
          >
            ✕
          </button>
        </div>

        <section className="mb-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-800">熱中症搬送リスク度とは</h3>
          <p className="text-xs leading-relaxed text-slate-600">
            その週・その県で熱中症により救急搬送された人数を、県の人口で割って
            <strong>人口10万人あたり</strong>に換算した値です。人数そのままだと
            人口の多い都市ほど大きく見えてしまうため、人口で割ることで
            <strong>都道府県を公平に比較</strong>できます。
          </p>
          <div className="mt-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-700">
            {formula}
          </div>
        </section>

        <section className="mb-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-800">色とドットの意味</h3>
          <ul className="list-disc space-y-1 pl-4 text-xs leading-relaxed text-slate-600">
            <li>
              <strong>地図の塗り色</strong> = 熱中症搬送リスク度（青=低い 〜 暗赤=極めて高い）
            </li>
            <li>
              <strong>丸数字</strong> = その週の最高気温（℃、予測タブと同じ配色）
            </li>
            <li>
              丸数字の下の <span style={{ color: '#e60012' }}>↑</span>/
              <span style={{ color: '#1d8eff' }}>↓</span> = 前の週からの気温変化
            </li>
          </ul>
        </section>

        <section className="mb-4">
          <h3 className="mb-1 text-sm font-semibold text-slate-800">この指標の狙い</h3>
          <p className="text-xs leading-relaxed text-slate-600">
            気温が高い週ほどリスク度も高い傾向はありますが、
            <strong>気温が特別高くなくてもリスク度が高い県・週</strong>が存在します。
            暑熱順化の遅れ・高齢化率・湿度などが影響するためです。
            「気温だけでは測れない熱中症リスク」を示すエビデンスとして活用できます。
          </p>
        </section>

        <section>
          <h3 className="mb-1 text-sm font-semibold text-slate-800">データの出典</h3>
          <ul className="space-y-0.5 text-[11px] leading-relaxed text-slate-500">
            <li>・最高気温: 気象庁 過去の気象データ（各県代表気象台の日最高気温の週内最大値）</li>
            <li>・救急搬送人員: 総務省消防庁 熱中症による救急搬送状況（週内合計）</li>
            <li>
              ・人口: {popSrc?.label ?? '総務省統計局 人口推計'}
              {popSrc?.appliedTo ? `（${popSrc.appliedTo}）` : ''}
            </li>
            <li>・地図タイル: 地図データ © 国土地理院（淡色地図）</li>
            <li>・対象: 各年 5/1〜10/1 を 7 日区切りで 22 週</li>
          </ul>
        </section>
      </div>
    </div>
  );
}

function renderPopupHtml(p: {
  code: string;
  year: string;
  weekLabel: string;
  prefName: string;
  maxTemp: number | null;
  prevTemp: number | null;
  risk: number | null;
  transports: number;
  population: number | undefined;
  rank: number | null;
  total: number | null;
}): string {
  const t = p.maxTemp == null ? '—' : `${p.maxTemp.toFixed(1)}℃`;
  let deltaStr = '';
  if (p.maxTemp != null && p.prevTemp != null) {
    const d = p.maxTemp - p.prevTemp;
    if (d > 0.05) deltaStr = ` <span style="color:#e60012;">⬆${d.toFixed(1)}</span>`;
    else if (d < -0.05) deltaStr = ` <span style="color:#1d8eff;">⬇${Math.abs(d).toFixed(1)}</span>`;
    else deltaStr = ` <span style="color:#64748b;">±0.0</span>`;
  }
  const risk = p.risk == null ? '—' : p.risk.toFixed(1);
  const rl = riskLabel(p.risk);
  const rankStr =
    p.rank != null && p.total != null ? `全国 ${p.rank}位 / ${p.total}` : '';
  const popStr = p.population != null ? `（搬送 ${p.transports.toLocaleString('ja-JP')}人 / 人口 ${(p.population / 10000).toFixed(0)}万人）` : '';
  return `
    <div style="padding:6px 4px;font-family:system-ui,sans-serif;min-width:200px;">
      <div style="font-weight:600;font-size:14px;color:#0f172a;">${escapeHtml(p.prefName)}</div>
      <div style="font-size:11px;color:#475569;margin-top:2px;">${escapeHtml(p.year)}年 ${escapeHtml(p.weekLabel)}</div>
      <div style="margin-top:8px;display:flex;flex-direction:column;gap:6px;">
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;">
          <span style="color:#64748b;font-size:11px;">週内最高気温</span>
          <span style="font-weight:600;color:#0f172a;font-size:14px;">${t}${deltaStr}</span>
        </div>
        <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;">
          <span style="color:#64748b;font-size:11px;">熱中症搬送リスク度</span>
          <span style="font-weight:700;color:#b91c1c;font-size:16px;">${risk}<span style="font-size:10px;font-weight:400;color:#94a3b8;"> /10万・週</span></span>
        </div>
        <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
          <span style="font-size:11px;color:#0f172a;">${escapeHtml(rl)}</span>
          <span style="font-size:10px;color:#94a3b8;">${escapeHtml(rankStr)}</span>
        </div>
        <div style="font-size:10px;color:#94a3b8;">${escapeHtml(popStr)}</div>
        <a href="/?view=chart&code=${encodeURIComponent(p.code)}&year=${encodeURIComponent(p.year)}"
           target="_blank" rel="noopener"
           style="display:inline-block;margin-top:4px;font-size:12px;color:#2563eb;text-decoration:underline;">
          週別グラフを表示（別タブ）
        </a>
      </div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function raiseTempLayers(map: maplibregl.Map): void {
  // Move temp layers to the very top, in draw order (circle → temp → delta).
  for (const id of [PT_CIRCLE, PT_TEMP_LABEL, PT_DELTA_LABEL]) {
    if (map.getLayer(id)) map.moveLayer(id);
  }
}

// Shares the forecast map's saved-view key so switching modes keeps position.
type SavedView = { center: [number, number]; zoom: number };
const VIEW_STORAGE_KEY = 'heat-pwa:map-view';

function loadSavedView(): SavedView | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (
      !Array.isArray(v.center) ||
      v.center.length !== 2 ||
      typeof v.center[0] !== 'number' ||
      typeof v.center[1] !== 'number' ||
      typeof v.zoom !== 'number'
    ) {
      return null;
    }
    return { center: [v.center[0], v.center[1]], zoom: v.zoom };
  } catch {
    return null;
  }
}

function saveView(v: SavedView): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(v));
  } catch {
    // quota / disabled storage — ignore
  }
}

function buildStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      raster: {
        type: 'raster',
        tiles: ACTIVE_TILE_SOURCE.tileUrls,
        tileSize: 256,
        attribution: ACTIVE_TILE_SOURCE.attribution,
        maxzoom: ACTIVE_TILE_SOURCE.maxZoom,
        minzoom: ACTIVE_TILE_SOURCE.minZoom,
      },
    },
    layers: [{ id: 'raster', type: 'raster', source: 'raster' }],
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  };
}
